import { useEffect, useState } from "react";

type IdleRequestId = number;

type IdleDeadline = { didTimeout: boolean; timeRemaining: () => number };

type IdleCallback = (deadline: IdleDeadline) => void;

type IdleAPI = {
  requestIdleCallback: (
    cb: IdleCallback,
    opts?: { timeout?: number }
  ) => IdleRequestId;
  cancelIdleCallback: (id: IdleRequestId) => void;
};

function getIdleApi(): IdleAPI {
  const w = window as any;
  // happy-dom does not implement requestIdleCallback, so the native-API path
  // (and its cancelIdleCallback?.bind ?? clearTimeout fallback) is unreachable
  // under the test runtime; only the setTimeout(16) fallback below executes.
  /* v8 ignore next 6 */
  if (typeof w.requestIdleCallback === "function") {
    return {
      requestIdleCallback: w.requestIdleCallback.bind(w),
      cancelIdleCallback: w.cancelIdleCallback?.bind(w) ?? clearTimeout
    };
  }
  return {
    requestIdleCallback: (cb) =>
      setTimeout(
        () =>
          cb({
            didTimeout: false,
            // The hook's idle callback (below) ignores the deadline arg, so
            // this fabricated timeRemaining is never invoked by the sole caller.
            /* v8 ignore next */
            timeRemaining: () => 0
          }),
        16
      ) as unknown as IdleRequestId,
    cancelIdleCallback: (id) => clearTimeout(id as unknown as NodeJS.Timeout)
  };
}

/**
 * Progressively reveals `items` in batches of `chunkSize`, yielding to the
 * browser between batches via requestIdleCallback. Prevents long tasks when
 * rendering 1000+ heavy rows. `onComplete` fires once all items are visible.
 */
export function useChunkedRender<T>(
  items: T[],
  chunkSize: number,
  onComplete?: () => void
): { visible: T[]; isComplete: boolean; progress: number } {
  const [visibleCount, setVisibleCount] = useState(() =>
    Math.min(chunkSize, items.length)
  );

  // Reset when inputs change (different dataset or chunk size).
  useEffect(() => {
    setVisibleCount(Math.min(chunkSize, items.length));
  }, [items, chunkSize]);

  useEffect(() => {
    if (visibleCount >= items.length) {
      if (items.length > 0) onComplete?.();
      return undefined;
    }
    const { requestIdleCallback, cancelIdleCallback } = getIdleApi();
    const handle = requestIdleCallback(
      () => {
        setVisibleCount((v) => Math.min(v + chunkSize, items.length));
      },
      { timeout: 200 }
    );
    return () => cancelIdleCallback(handle);
  }, [visibleCount, items.length, chunkSize, onComplete]);

  return {
    visible: items.slice(0, visibleCount),
    isComplete: visibleCount >= items.length,
    progress: items.length > 0 ? visibleCount / items.length : 1
  };
}
